import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { getBlogPostBySlug } from "../data/blogPosts";
import { HiArrowLeft, HiCalendar } from "react-icons/hi";
import SEO from "../components/SEO";

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        image={`https://hexapi.tech${post.image}`}
        url={`https://hexapi.tech/blog/${post.slug}`}
        type="article"
        article={{
          title: post.title,
          excerpt: post.excerpt,
          image: `https://hexapi.tech${post.image}`,
          date: post.date,
        }}
      />
      <div className="pt-24 lg:pt-28 pb-16 min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-4 lg:px-8">
        {/* Back Link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600
                     font-medium mb-8 transition-colors"
        >
          <HiArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        {/* Post Header */}
        <header className="mb-8">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-600
                          text-sm font-medium rounded-full mb-4">
            Announcement
          </span>
          <h1 className="text-3xl lg:text-4xl font-bold text-neutral-dark mb-5">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-neutral">
            <span className="flex items-center gap-2">
              <HiCalendar className="w-5 h-5 text-primary-400" />
              {post.date}
            </span>
            {/* <span className="flex items-center gap-2">
              <HiUser className="w-5 h-5 text-primary-400" />
              {post.author}
            </span> */}
          </div>
        </header>

        {/* Featured Image */}
        <div className="rounded-2xl overflow-hidden mb-10 shadow-card">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-64 lg:h-80 object-cover"
          />
        </div>

        {/* Post Content */}
        <div className="prose prose-lg max-w-none">
          {post.content.split("\n").map((paragraph, index) => {
            const trimmed = paragraph.trim();
            if (!trimmed) return null;

            // Handle headings
            if (trimmed.startsWith("## ")) {
              return (
                <h2
                  key={index}
                  className="text-2xl font-bold text-neutral-dark mt-10 mb-4"
                >
                  {trimmed.replace("## ", "")}
                </h2>
              );
            }
            if (trimmed.startsWith("### ")) {
              return (
                <h3
                  key={index}
                  className="text-lg font-semibold text-neutral-dark mt-6 mb-3"
                >
                  {trimmed.replace("### ", "")}
                </h3>
              );
            }

            // Handle blockquotes
            if (trimmed.startsWith("> ")) {
              return (
                <blockquote
                  key={index}
                  className="border-l-4 border-primary-400 pl-4 py-2 my-6 bg-primary-50 rounded-r-lg italic text-neutral-dark"
                >
                  {trimmed.replace("> ", "").replace(/\*\*/g, "")}
                </blockquote>
              );
            }

            // Handle list items
            if (trimmed.startsWith("- ")) {
              return (
                <li key={index} className="text-neutral ml-6 mb-2 list-disc">
                  {trimmed.replace("- ", "").replace(/\*\*/g, "")}
                </li>
              );
            }

            // Handle bold text in paragraphs
            const boldRegex = /\*\*(.*?)\*\*/g;
            const parts = trimmed.split(boldRegex);

            return (
              <p key={index} className="text-neutral mb-4 leading-relaxed">
                {parts.map((part, i) =>
                  i % 2 === 1 ? (
                    <strong key={i} className="font-semibold text-neutral-dark">
                      {part}
                    </strong>
                  ) : (
                    part
                  )
                )}
              </p>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-14 p-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl text-center text-white">
          <h3 className="text-2xl font-bold mb-3">
            Interested in Learning More?
          </h3>
          <p className="text-primary-100 mb-6 max-w-lg mx-auto">
            Contact us to discuss how we can bring this curriculum to your institution
            and transform your school into a hub of innovation.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center px-6 py-3 bg-white text-primary-600
                       font-medium rounded-lg hover:bg-primary-50 transition-colors"
          >
            Get in Touch
          </Link>
        </div>

        {/* Share / Navigation */}
        <div className="mt-10 pt-8 border-t border-gray-200 flex justify-between items-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-neutral hover:text-primary-500 transition-colors"
          >
            <HiArrowLeft className="w-4 h-4" />
            All Posts
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral hover:text-primary-500 transition-colors"
          >
            Home
          </Link>
        </div>
      </article>
      </div>
    </>
  );
};

export default BlogPostPage;
